import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CartManagementMode, FinancingPlanType } from '@flexyoo/api/models';
import { UserFinancingPlansService } from '@flexyoo/employee/data-access';
import { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import {
  EmployeeSelectedBenefitsActions,
  employeeSelectedBenefitsSelectors,
} from '@flexyoo/employee/store';
import { isEmployeeCartVisible } from '@flexyoo/employee/util';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import { EmployeeBasketCartArchiveComponent } from '../employee-basket-cart-archive/employee-basket-cart-archive.component';
import { EmployeeBasketCartManagementComponent } from '../employee-basket-cart-management/employee-basket-cart-management.component';
import { EmployeeBasketCartComponent } from '../employee-basket-cart/employee-basket-cart.component';
import { EmployeeBasketPriorYearPaymentsComponent } from '../employee-basket-prior-year-payments/employee-basket-prior-year-payments.component';
import { EmployeeCurrentBenefitsComponent } from '../employee-current-benefits/employee-current-benefits.component';
import { EmployeePbBasketInfoComponent } from '../employee-pb-basket-info/employee-pb-basket-info.component';
import { EmployeeSsBasketInfoComponent } from '../employee-ss-basket-info/employee-ss-basket-info.component';

@Component({
  selector: 'flexyoo-employee-basket-view',
  templateUrl: './employee-basket-view.component.html',
  styleUrl: './employee-basket-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmployeePbBasketInfoComponent,
    EmployeeSsBasketInfoComponent,
    EmployeeCurrentBenefitsComponent,
    EmployeeBasketPriorYearPaymentsComponent,
    EmployeeBasketCartComponent,
    EmployeeBasketCartArchiveComponent,
    EmployeeBasketCartManagementComponent,
  ],
})
export class EmployeeBasketViewComponent {
  private readonly userFinancingPlansService = inject(UserFinancingPlansService);
  private readonly store = inject(Store);

  readonly selectedBenefitId = input<number | null>(null);
  readonly isCartSigningOpen = input(false);
  readonly removeBenefit = output<EmployeeSelectedBenefit>();
  readonly signBenefit = output<EmployeeSelectedBenefit>();
  readonly signCart = output<number>();
  readonly refresh = output<void>();

  private readonly basketCart = viewChild(EmployeeBasketCartComponent);
  private readonly cartManagement = viewChild(EmployeeBasketCartManagementComponent);

  protected readonly isCartMode = toSignal(
    this.store
      .select(tenantSettingsSelectors.selectCartManagementMode)
      .pipe(map((mode) => mode === CartManagementMode.Cart)),
    { initialValue: false }
  );

  protected readonly hasCart = toSignal(
    this.store
      .select(employeeSelectedBenefitsSelectors.selectCurrentCartOrSimulatedDraft)
      .pipe(map((cart) => !!cart && isEmployeeCartVisible(cart.state))),
    { initialValue: false }
  );

  protected readonly isPbEnabled = toSignal(
    this.userFinancingPlansService.userActivePlansInfo$.pipe(
      map((info) => info[FinancingPlanType.PrimesAndBonuses])
    )
  );
  protected readonly isSsEnabled = toSignal(
    this.userFinancingPlansService.userActivePlansInfo$.pipe(
      map((info) => info[FinancingPlanType.SalarySacrifice])
    )
  );

  constructor() {
    inject(Actions)
      .pipe(
        ofType(
          EmployeeSelectedBenefitsActions.removeBenefitSuccess,
          EmployeeSelectedBenefitsActions.clearCartSuccess
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.refresh.emit();
      });
  }

  expandCart(): void {
    this.cartManagement()?.expandCart();
    this.basketCart()?.expandCart();
  }

  protected onRemoveBenefit(benefit: EmployeeSelectedBenefit): void {
    this.removeBenefit.emit(benefit);
  }

  protected onSignBenefit(benefit: EmployeeSelectedBenefit): void {
    this.signBenefit.emit(benefit);
  }

  protected onSignCart(cartId: number): void {
    this.signCart.emit(cartId);
  }
}
